/**
 * KAN-931: аккаунты MAX кабинета: список, подключение токеном, вкл/выкл.
 * После подключения и переключения список перечитывается. Права как у
 * Telegram-аккаунтов: запрос, закрытый пользователю, не выполняется.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useCanCallApi } from '@/hooks/useApiAccess';
import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { maxAccountsApi, type MaxImportTokenFields } from '@/services/api/maxAccounts';
import { MAX_ACCOUNTS_URL } from '@/services/api/maxAccountsUrl';

export const MAX_ACCOUNTS_KEY = ['max-accounts'] as const;

/** Любой id для проверки права PATCH на аккаунт. */
const ANY_ACCOUNT = '0';

export function useMaxAccounts() {
  const queryClient = useQueryClient();
  const canList = useCanCallApi('GET', MAX_ACCOUNTS_URL);
  const canImport = useCanCallApi('POST', `${MAX_ACCOUNTS_URL}import/`);
  const canToggle = useCanCallApi('PATCH', `${MAX_ACCOUNTS_URL}${ANY_ACCOUNT}/`);

  const accounts = useQuery({
    queryKey: MAX_ACCOUNTS_KEY,
    queryFn: maxAccountsApi.list,
    enabled: canList,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: MAX_ACCOUNTS_KEY });

  const importToken = useMutation({
    mutationFn: (fields: MaxImportTokenFields) => maxAccountsApi.importToken(fields),
    onSuccess: () => {
      toast.success('Аккаунт MAX подключён');
      void refresh();
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось подключить аккаунт MAX')),
  });

  const setEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      maxAccountsApi.setEnabled(id, enabled),
    onError: (err) => toast.error(extractErrorMessage(err, 'Не удалось изменить аккаунт MAX')),
    onSettled: () => void refresh(),
  });

  return { canList, canImport, canToggle, accounts, importToken, setEnabled };
}
